import React, { Suspense, lazy } from 'react';
import 'nprogress/nprogress.css';
import nprogress from 'nprogress';
import Guard, { GuestGuard } from './Guard';
import Authenticated from './Layouts/Authenticated';
import { Routes, Route, useLocation } from 'react-router-dom';

// Eagerly loaded — these are tiny and needed immediately
import Home from './Pages/Home/Home';
import ErrorPage from './Pages/Error/Index';
import PageLoader from './Components/Shared/PageLoader';

import AuthSkeleton from './Components/Shared/AuthSkeleton';

// Resilient lazy loader to handle new deployments & stale chunk caches automatically
const lazyRetry = (componentImport) =>
  lazy(async () => {
    const pageHasBeenRefreshed = JSON.parse(
      window.sessionStorage.getItem('chunk_retry_refreshed') || 'false'
    );

    try {
      const component = await componentImport();
      window.sessionStorage.setItem('chunk_retry_refreshed', 'false');
      return component;
    } catch (error) {
      if (!pageHasBeenRefreshed) {
        window.sessionStorage.setItem('chunk_retry_refreshed', 'true');
        window.location.reload();
        return { default: () => null };
      }
      throw error;
    }
  });

// Lazy-loaded route-level components — each becomes its own JS chunk
const Login                  = lazyRetry(() => import('./Pages/Auth/Login'));
const Register               = lazyRetry(() => import('./Pages/Auth/Register'));
const ForgotPassword         = lazyRetry(() => import('./Pages/Auth/ForgotPassword'));
const DesignSystem           = lazyRetry(() => import('./Pages/DesignSystem/DesignSystem'));
const Dashboard              = lazyRetry(() => import('./Pages/Dashboard/Dashboard'));
const Profile                = lazyRetry(() => import('./Pages/MyAccount/Profile'));
const ChangePassword         = lazyRetry(() => import('./Pages/MyAccount/ChangePassword'));
const Configuration          = lazyRetry(() => import('./Pages/MyAccount/Configuration'));
const RolesIndex             = lazyRetry(() => import('./Pages/Master/Roles/Index'));
const RolesCreate            = lazyRetry(() => import('./Pages/Master/Roles/Create'));
const RolesEdit              = lazyRetry(() => import('./Pages/Master/Roles/Edit'));
const RolesStatuses          = lazyRetry(() => import('./Pages/Master/Roles/Statuses'));
const RequestStatusIndex     = lazyRetry(() => import('./Pages/Master/RequestStatus/Index'));
const WOSIndex               = lazyRetry(() => import('./Pages/Master/WorkOrderStatus/Index'));
const UtilitiesIndex         = lazyRetry(() => import('./Pages/Master/Utilities/Index'));
const UtilityDepartmentIndex = lazyRetry(() => import('./Pages/Master/Utilities/Departments/Index'));
const SubDepartmentIndex     = lazyRetry(() => import('./Pages/Master/Utilities/Departments/SubDepartments/Index'));
const UtilitySevicesIndex    = lazyRetry(() => import('./Pages/Master/Utilities/Services/Index'));
const ServiceRequestLevelIndex = lazyRetry(() => import('./Pages/Master/ServiceRequestLevel/Index'));
const ServiceDefinitionsIndex = lazyRetry(() => import('./Pages/Master/ServiceDefinitions/Index'));
const ReportsIndex           = lazyRetry(() => import('./Pages/Reports/Index'));
const SettingsIndex          = lazyRetry(() => import('./Pages/Settings/Index'));
const AssignedWorkOrdersIndex = lazyRetry(() => import('./Pages/WorkOrders/AssignedWorkOrdersIndex'));
const CustomersIndex         = lazyRetry(() => import('./Pages/Customers/Index'));
const LeadsIndex             = lazyRetry(() => import('./Pages/Leads/Index'));
const RequestsIndex          = lazyRetry(() => import('./Pages/Requests/Index'));
const UsersIndex             = lazyRetry(() => import('./Pages/Users/Index'));
const UserPermissionForm     = lazyRetry(() => import('./Pages/Users/UserRights/Permission'));
const SelectVendor           = lazyRetry(() => import('./Pages/Vendor/SelectVendor'));
const VendorDashboard        = lazyRetry(() => import('./Pages/Vendor/Dashboard'));
const VendorProfile          = lazyRetry(() => import('./Pages/Vendor/Profile'));
const VendorTechnicians      = lazyRetry(() => import('./Pages/Vendor/Technicians'));
const VendorServiceRequests  = lazyRetry(() => import('./Pages/Vendor/ServiceRequests'));
const PricingSettings        = lazyRetry(() => import('./Pages/Vendor/PricingSettings'));
const AdminVendorSubscriptions = lazyRetry(() => import('./Pages/Vendor/AdminVendorSubscriptions'));
const AdminProducts           = lazyRetry(() => import('./Pages/Marketplace/AdminProducts'));
const AdminOrders             = lazyRetry(() => import('./Pages/Marketplace/AdminOrders'));
const AdminBills              = lazyRetry(() => import('./Pages/Billing/AdminBills'));
const AdminReviews            = lazyRetry(() => import('./Pages/Reviews/AdminReviews'));


const AppRoutes = () => {
    const location = useLocation();

    React.useEffect(() => {
        nprogress.start();
        nprogress.done();
    }, [location.pathname]);

    return (
        <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/design-system" element={<Suspense fallback={<PageLoader />}><DesignSystem /></Suspense>} />

            {/* Auth Routes */}
            <Route path="/" element={<GuestGuard />}>
                <Route path="login" element={<Suspense fallback={<AuthSkeleton />}><Login /></Suspense>} />
                <Route path="register" element={<Suspense fallback={<AuthSkeleton isRegister={true} />}><Register /></Suspense>} />
                <Route path="forgot-password" element={<Suspense fallback={<AuthSkeleton />}><ForgotPassword /></Suspense>} />
            </Route>

            {/* Authenticated Routes — Suspense lives inside Authenticated so sidebar never unmounts */}
            <Route element={<Guard />}>
                <Route element={<Authenticated />}>
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/profile" element={<Profile />} />
                    <Route path="/change-password" element={<ChangePassword />} />
                    <Route path="/configuration" element={<Configuration />} />
                    <Route path="/roles" element={<RolesIndex />} />
                    <Route path="/roles/create" element={<RolesCreate />} />
                    <Route path="/roles/edit/:id" element={<RolesEdit />} />
                    <Route path="/roles/statuses" element={<RolesStatuses />} />
                    <Route path="/requeststatus" element={<RequestStatusIndex />} />
                    <Route path="/workorderstatuses" element={<WOSIndex />} />
                    <Route path="/utilities" element={<UtilitiesIndex />} />
                    <Route path="/utilities/:utility_id/departments" element={<UtilityDepartmentIndex />} />
                    <Route path="/utilities/:utility_id/departments/:department_id/sub_departments" element={<SubDepartmentIndex />} />
                    <Route path="/utilities/:utility_id/services" element={<UtilitySevicesIndex />} />
                    <Route path="/service-request-services" element={<ServiceRequestLevelIndex />} />
                    <Route path="/service-definitions" element={<Suspense fallback={<PageLoader />}><ServiceDefinitionsIndex /></Suspense>} />
                    <Route path="/reports" element={<ReportsIndex />} />
                    <Route path="/settings" element={<SettingsIndex />} />
                    <Route path="/assignedworkorders" element={<AssignedWorkOrdersIndex />} />
                    <Route path="/customers" element={<CustomersIndex />} />
                    <Route path="/leads" element={<Suspense fallback={<PageLoader />}><LeadsIndex /></Suspense>} />
                    <Route path="/requests" element={<RequestsIndex />} />
                    <Route path="/users" element={<UsersIndex />} />
                    <Route path="/users/:id/rights" element={<UserPermissionForm />} />

                    {/* Vendor Routes */}
                    <Route path="/vendor/select" element={<SelectVendor />} />
                    <Route path="/vendors" element={<SelectVendor />} />
                    <Route path="/vendor/dashboard" element={<VendorDashboard />} />
                    <Route path="/vendor/profile" element={<VendorProfile />} />
                    <Route path="/vendor/technicians" element={<VendorTechnicians />} />
                    <Route path="/vendor/service-requests" element={<VendorServiceRequests />} />
                    <Route path="/vendor-pricing-settings" element={<PricingSettings />} />
                    <Route path="/vendor-subscriptions" element={<AdminVendorSubscriptions />} />
                    <Route path="/admin/vendor-subscriptions" element={<AdminVendorSubscriptions />} />

                    {/* Super Admin Marketplace, Billing, Reviews Oversight */}
                    <Route path="/admin-products" element={<AdminProducts />} />
                    <Route path="/products" element={<AdminProducts />} />
                    <Route path="/admin-orders" element={<AdminOrders />} />
                    <Route path="/orders" element={<AdminOrders />} />
                    <Route path="/admin-bills" element={<AdminBills />} />
                    <Route path="/bills" element={<AdminBills />} />
                    <Route path="/admin-reviews" element={<AdminReviews />} />
                    <Route path="/reviews" element={<AdminReviews />} />

                </Route>
            </Route>

            <Route path="*" element={<ErrorPage status_code="404" message="Page not found!" />} />
        </Routes>
    );
};

export default AppRoutes;
